import { ref, nextTick, computed } from 'vue';
import type { Task, TaskPriority } from '~/types/models';
import { buildTaskAssigneePayload, resolveTaskAssigneeIds } from '~/lib/taskAssignees';

/**
 * useTaskTable Composable
 * 
 * Centralized logic for task table management including CRUD operations,
 * filtering, searching, and inline editing capabilities.
 * 
 * This composable follows the DRY principle by consolidating task table logic
 * that was previously duplicated across multiple pages.
 * 
 * Features:
 * - Task creation with auto-population of project/client context
 * - Inline editing of task fields
 * - Delete operations with error handling
 * - Search functionality (filters by task title or internal tags)
 * - Multi-criteria filtering (status, priority, assignee, project, client)
 * - Keyboard shortcuts (Enter to save, Escape to cancel)
 * 
 * @param options - Optional context for auto-populating projectId/clientId
 * @param options.projectId - Pre-populate new tasks with this project ID
 * @param options.clientId - Pre-populate new tasks with this client ID
 * 
 * @example Usage in a project-specific page (auto-populates projectId)
 * const { filteredTasks, searchQuery, filters, ... } = useTaskTable({
 *   projectId: currentProject.id,
 *   clientId: currentProject.clientId
 * });
 * 
 * @example Usage in all-tasks page (no auto-population)
 * const { filteredTasks, searchQuery, filters, ... } = useTaskTable();
 */
export function useTaskTable(options?: { projectId?: string; clientId?: string }) {
    const taskStore = useTaskStore();
    const projectStore = useProjectStore();
    const clientStore = useClientStore();
    const entityTagsStore = useEntityTagsStore();
    const {
        statusOptions,
        statusOptionDetails,
        assigneeOptions,
        priorityOptions,
    } = useTaskOptionCatalog();
    
    // ========================================================================
    // STATE MANAGEMENT
    // ========================================================================
    
    /**
     * New task being created
     * Pre-populated with projectId/clientId if provided in options
     */
    const newTask = ref({ 
        title: '', 
        status: '', 
        assignee: '', 
        assigneeId: '',
        assigneeIds: [] as string[],
        assignees: [] as Array<{ id: string; label: string; avatarURL?: string }>,
        priority: '' as TaskPriority | '',
        startDate: '',
        dueDate: '',
        clientVisible: false,
        projectId: options?.projectId || '',
        milestoneId: '',
        clientId: options?.clientId || ''
    });
    
    /** Whether the "add new task" row is visible */
    const isAddingTask = ref(false);
    
    /** ID of the task currently being edited (null if none) */
    const editingTaskId = ref<string | null>(null);
    
    /** Field name being edited (e.g., 'title', 'status') */
    const editingField = ref<string | null>(null);
    
    /** Current value in the edit input */
    const editingValue = ref('');
    
    /** Loading state for async operations */
    const loading = ref(false);
    
    // ========================================================================
    // SEARCH & FILTER STATE
    // ========================================================================
    
    /**
     * Search query for filtering tasks by title or tag
     * Filters are case-insensitive and match partial strings
     */
    const searchQuery = ref('');
    
    /**
     * Active filters for multi-criteria task filtering
     * Empty string means "no filter" for that criterion
     */
    const filters = ref({
        status: '',
        priority: '',
        assigneeId: '',
        projectId: '',
        clientId: '',
        tag: '',
    });
    
    // ========================================================================
    // DROPDOWN OPTIONS
    // ========================================================================
    
    /** Projects from store (for project select dropdown) */
    const projectOptions = computed(() => projectStore.projects || []);
    
    /** Clients from store (for client select dropdown) */
    const clientOptions = computed(() => clientStore.clients || []);
    // ========================================================================
    // COMPUTED PROPERTIES
    // ========================================================================
    
    /**
     * Filtered and searched tasks
     * 
     * Applies search query and all active filters to the task list.
     * Filters are applied cumulatively (AND logic, not OR).
     * 
     * Search logic:
     * - Case-insensitive
     * - Matches partial strings in task title or assigned tags
     * 
     * Filter logic:
     * - Only applies filters that have a non-empty value
     * - Exact match for status, priority, assignee
     * - Exact match for projectId and clientId
     * 
     * @returns Filtered array of tasks matching all criteria
     */
    const filteredTasks = computed(() => {
        let tasks = taskStore.tasks;
        
        // Apply search query (filter by title)
        if (searchQuery.value.trim()) {
            const query = searchQuery.value.toLowerCase();
            tasks = tasks.filter(task => (
                task.title?.toLowerCase().includes(query)
                || entityTagsStore.tagsFor('task', task.id).some(tag => tag.toLowerCase().includes(query))
            ));
        }
        
        // Apply status filter
        if (filters.value.status) {
            tasks = tasks.filter(task => task.status === filters.value.status);
        }
        
        // Apply priority filter
        if (filters.value.priority) {
            tasks = tasks.filter(task => task.priority === filters.value.priority);
        }
        
        // Apply assignee filter
        if (filters.value.assigneeId) {
            tasks = tasks.filter(task => resolveTaskAssigneeIds(task).includes(filters.value.assigneeId));
        }
        
        // Apply project filter
        if (filters.value.projectId) {
            tasks = tasks.filter(task => task.projectId === filters.value.projectId);
        }
        
        // Apply client filter
        if (filters.value.clientId) {
            tasks = tasks.filter(task => String(task.clientId || '').trim() === filters.value.clientId);
        }

        if (filters.value.tag) {
            tasks = tasks.filter(task => entityTagsStore.hasTag('task', task.id, filters.value.tag));
        }
        
        return tasks;
    });
    
    /**
     * Check if any filters are active
     * Used to show "clear filters" button and empty state context
     */
    const hasActiveFilters = computed(() => {
        return !!(
            searchQuery.value.trim() ||
            filters.value.status ||
            filters.value.priority ||
            filters.value.assigneeId ||
            filters.value.projectId ||
            filters.value.clientId ||
            filters.value.tag
        );
    });
    
    // ========================================================================
    // FILTER MANAGEMENT
    // ========================================================================
    
    /**
     * Clear all active filters and search query
     * Useful for "reset" or "show all tasks" functionality
     */
    function clearAllFilters() {
        searchQuery.value = '';
        filters.value = {
            status: '',
            priority: '',
            assigneeId: '',
            projectId: '',
            clientId: '',
            tag: '',
        };
    }
    
    // ========================================================================
    // TASK CREATION HANDLERS
    // ========================================================================
    
    /**
     * Enter "add task" mode
     * 
     * Shows the new task row and auto-focuses the first input field
     * for better UX. Uses nextTick to ensure DOM is updated before focusing.
     */
    function startAddingTask() {
        isAddingTask.value = true;
        nextTick(() => {
            const firstInput = document.querySelector('.new-task-row input');
            if (firstInput) (firstInput as HTMLInputElement).focus();
        });
    }
    
    /**
     * Save the new task to Firestore
     * 
     * Process:
     * 1. Set loading state
     * 2. Call task store to persist to database
     * 3. Reset form while preserving context (projectId/clientId)
     * 4. Exit add mode
     * 5. Clear loading state
     * 
     * Note: projectId and clientId are preserved in the reset so that
     * when adding multiple tasks in a project context, the user doesn't
     * have to re-select the project each time.
     * 
     * @param userId - ID of the user creating the task
     * @throws Logs error and re-throws for parent component to handle
     */
    async function saveNewTask(userId: string) {
        loading.value = true;
        try {
            const { priority, assigneeIds, assignees, assigneeId, assignee, ...restTask } = newTask.value;
            const assigneePayload = buildTaskAssigneePayload(assigneeIds, assigneeOptions.value);
            const payload = {
                ...restTask,
                ...assigneePayload,
                ...(priority ? { priority } : {})
            };
            const taskId = await taskStore.addTask(payload, userId);
            
            // Reset form but preserve context
            newTask.value = { 
                title: '', 
                status: '', 
                assignee: '', 
                assigneeId: '',
                assigneeIds: [] as string[],
                assignees: [] as Array<{ id: string; label: string; avatarURL?: string }>,
                priority: '' as TaskPriority | '',
                startDate: '',
                dueDate: '',
                clientVisible: false,
                projectId: options?.projectId || '',
                milestoneId: '',
                clientId: options?.clientId || ''
            };
            
            isAddingTask.value = false;
            return taskId;
        } catch (error) {
            console.error('Error saving task:', error);
            throw error;
        } finally {
            loading.value = false;
        }
    }
    
    /**
     * Cancel task creation
     * 
     * Resets the form and exits add mode without saving.
     * Preserves projectId/clientId context for convenience.
     */
    function cancelAddingTask() {
        newTask.value = { 
            title: '', 
            status: '', 
            assignee: '', 
            assigneeId: '',
            assigneeIds: [] as string[],
            assignees: [] as Array<{ id: string; label: string; avatarURL?: string }>,
            priority: '' as TaskPriority | '',
            startDate: '',
            dueDate: '',
            clientVisible: false,
            projectId: options?.projectId || '',
            milestoneId: '',
            clientId: options?.clientId || ''
        };
        isAddingTask.value = false;
    }
    
    /**
     * Keyboard handler for new task row
     * 
     * Provides keyboard shortcuts:
     * - Enter: Save the task
     * - Escape: Cancel and exit add mode
     * 
     * @param event - Keyboard event from input field
     * @param userId - ID of user creating task (needed for save)
     */
    function handleAddKeyPress(event: KeyboardEvent, userId: string) {
        if (event.key === 'Enter') {
            saveNewTask(userId);
        } else if (event.key === 'Escape') {
            cancelAddingTask();
        }
    }
    
    // ========================================================================
    // INLINE EDITING HANDLERS
    // ========================================================================
    
    /**
     * Enter edit mode for a specific task field
     * 
     * Enables inline editing by:
     * 1. Storing which task and field are being edited
     * 2. Pre-populating the edit input with current value
     * 3. Auto-focusing the input field
     * 
     * @param taskId - ID of the task being edited
     * @param field - Name of field being edited (e.g., 'title', 'status')
     * @param value - Current value of the field
     */
    function startEditingField(taskId: string, field: string, value: string) {
        editingTaskId.value = taskId;
        editingField.value = field;
        editingValue.value = value;
        
        // Focus the input after DOM updates
        nextTick(() => {
            const input = document.querySelector(`.edit-input-${taskId}`) as HTMLInputElement;
            if (input) input.focus();
        });
    }
    
    /**
     * Save the edited field value
     * 
     * Updates only the specific field that was edited (partial update)
     * rather than updating the entire task object.
     * 
     * Process:
     * 1. Build partial update object with just the changed field
     * 2. Call store to persist to Firestore
     * 3. Clear edit state
     * 
     * @throws Logs error and re-throws for parent to handle
     */
    async function saveEditedField() {
        if (!editingTaskId.value || !editingField.value) return;
        
        try {
            const parsedValue = editingField.value === 'clientVisible'
                ? editingValue.value === 'true'
                : editingField.value === 'assigneeIds'
                    ? (() => {
                        try {
                            return JSON.parse(editingValue.value || '[]');
                        } catch {
                            return [];
                        }
                    })()
                    : editingValue.value;

            // Build partial update object
            const updates: Record<string, any> = { [editingField.value]: parsedValue };

            if (editingField.value === 'assigneeId' || editingField.value === 'assigneeIds') {
                const assigneeIds = editingField.value === 'assigneeIds'
                    ? (Array.isArray(parsedValue) ? parsedValue : [])
                    : (String(parsedValue || '').trim() ? [String(parsedValue)] : []);
                Object.assign(updates, buildTaskAssigneePayload(assigneeIds, assigneeOptions.value));
            }
            
            await taskStore.updateTask(editingTaskId.value, updates);
            
            // Clear edit state
            editingTaskId.value = null;
            editingField.value = null;
            editingValue.value = '';
        } catch (error) {
            console.error('Error updating task:', error);
            throw error;
        }
    }
    
    /**
     * Cancel editing without saving
     * 
     * Exits edit mode and discards changes
     */
    function cancelEditingField() {
        editingTaskId.value = null;
        editingField.value = null;
        editingValue.value = '';
    }
    
    /**
     * Keyboard handler for edit inputs
     * 
     * Provides keyboard shortcuts:
     * - Enter: Save changes
     * - Escape: Cancel and discard changes
     * 
     * @param event - Keyboard event from input field
     */
    function handleEditKeyPress(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            saveEditedField();
        } else if (event.key === 'Escape') {
            cancelEditingField();
        }
    }
    
    // ========================================================================
    // DELETE HANDLER
    // ========================================================================
    
    /**
     * Delete a task
     * 
     * Removes the task from Firestore and updates local state.
     * Parent component should typically show a confirmation dialog
     * before calling this function.
     * 
     * @param taskId - ID of task to delete
     * @throws Logs error and re-throws for parent to handle
     */
    async function deleteTask(taskId: string) {
        try {
            await taskStore.deleteTask(taskId);
        } catch (error) {
            console.error('Error deleting task:', error);
            throw error;
        }
    }
    
    // ========================================================================
    // PUBLIC API
    // ========================================================================
    
    return {
        // Core state
        newTask,
        isAddingTask,
        editingTaskId,
        editingField,
        editingValue,
        loading,
        
        // Search & Filter state
        searchQuery,
        filters,
        filteredTasks,
        hasActiveFilters,
        
        // Dropdown options
        statusOptions,
        statusOptionDetails,
        assigneeOptions,
        priorityOptions,
        projectOptions,
        clientOptions,
        
        // Task creation handlers
        startAddingTask,
        saveNewTask,
        cancelAddingTask,
        handleAddKeyPress,
        
        // Inline editing handlers
        startEditingField,
        saveEditedField,
        cancelEditingField,
        handleEditKeyPress,
        
        // Other handlers
        deleteTask,
        clearAllFilters
    };
}
